// Ask a vision model what a short window of the original video shows. Sent
// inline rather than through the Files API: redraw's gemini-media does the same
// because a proxy endpoint need not implement Files, and a 15s 360p clip is
// small enough that an upload round-trip is pure latency.
import * as crypto from "node:crypto";
import * as fs from "node:fs";
import { CLIP_FPS, cutClip } from "./clip.js";
import { post } from "./net.js";

const SYSTEM = "你是剧本审核的视频证据核对器。只报告视频窗口中可直接观察或听到的事实；"
  + "无法确认时明确说明，不推测剧情。";

export interface WatchUsage {
  call_key: string; trace_key: string; provider: string; model: string;
  input_tokens: number; cached_input_tokens: number; output_tokens: number;
  reasoning_tokens: number; clip_bytes: number; wall_ms: number;
}

export interface WatchResult { answer: string; usage: WatchUsage }

export interface WatchOptions {
  apiKey: string;
  model?: string;
  baseUrl?: string;
  callKey: string;
}

/** Stable across retries and identical windows, so a ledger can dedupe. */
export function traceKey(video: string, window: string, model: string, question: string): string {
  return "watch:" + crypto.createHash("sha256")
    .update([video, window, model, question].join("\n")).digest("hex");
}

export async function watchWindow(
  video: string, from: string, to: string, question: string, opts: WatchOptions,
): Promise<WatchResult> {
  const model = opts.model ?? "gemini-3.7-flash";
  const base = opts.baseUrl ?? "https://generativelanguage.googleapis.com";
  const clip = await cutClip(video, from, to);
  const started = Date.now();
  try {
    const body = {
      contents: [{
        role: "user",
        parts: [
          {
            inline_data: { mime_type: "video/mp4", data: fs.readFileSync(clip.path, "base64") },
            video_metadata: { fps: CLIP_FPS },
          },
          { text: question },
        ],
      }],
      systemInstruction: { parts: [{ text: SYSTEM }] },
      generationConfig: {
        temperature: 0.1,
        maxOutputTokens: 2048,
        responseMimeType: "application/json",
        responseSchema: {
          type: "object",
          properties: { answer: { type: "string" } },
          required: ["answer"],
        },
      },
    };
    const response = await post(
      `${base}/v1beta/models/${model}:generateContent?key=${opts.apiKey}`, body);
    const payload: any = await response.json().catch(() => ({}));
    if (!response.ok || payload?.error) {
      throw new Error(`media check failed (${response.status}): `
        + String(payload?.error?.message ?? "unknown").slice(0, 200));
    }
    const text = payload?.candidates?.[0]?.content?.parts
      ?.map((p: any) => p?.text).filter(Boolean).join("") ?? "";
    let answer = text.trim();
    try {
      const parsed = JSON.parse(answer);
      if (typeof parsed?.answer === "string") answer = parsed.answer;
    } catch { /* a model that ignored the schema still gave us prose */ }
    if (!answer) throw new Error("media check returned no answer");

    const meta = payload?.usageMetadata ?? {};
    return {
      answer,
      usage: {
        call_key: opts.callKey,
        trace_key: traceKey(video, `${from}..${to}`, model, question),
        provider: "google-gemini", model,
        input_tokens: Number(meta.promptTokenCount ?? 0),
        cached_input_tokens: Number(meta.cachedContentTokenCount ?? 0),
        output_tokens: Number(meta.candidatesTokenCount ?? 0),
        reasoning_tokens: Number(meta.thoughtsTokenCount ?? 0),
        clip_bytes: clip.bytes,
        wall_ms: Date.now() - started,
      },
    };
  } finally {
    clip.cleanup();
  }
}
